import {
  WORKSPACE_ACTIONS,
  WORKSPACE_BINDING_STATES,
  WORKSPACE_FILESYSTEM_KINDS,
  WORKSPACE_FILESYSTEM_STATES,
  WORKSPACE_INDEX_HEALTH,
  WORKSPACE_INDEX_PROGRESS,
  WORKSPACE_LIFECYCLES,
  WORKSPACE_OPERATION_KINDS,
  WORKSPACE_OPERATION_STATES,
  WORKSPACE_REFUSAL_REASONS,
  WORKSPACE_ROUTING_CLASSES,
  WORKSPACE_WORKSTATION_KINDS,
  WORKSPACE_WORKSTATION_STATUSES,
  type WorkspaceNameEntry,
  type WorkspaceProjection,
} from './types/workspace-state.js';

type JsonRecord = Record<string, unknown>;

function record(value: unknown, path: string): JsonRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as JsonRecord;
}

function exactKeys(value: JsonRecord, allowed: readonly string[], path: string): void {
  const allowedKeys = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedKeys.has(key))
      throw new Error(`${path}.${key} is not part of the workspace contract`);
  }
}

function nonEmptyString(value: unknown, path: string): asserts value is string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${path} must be a non-empty string`);
  }
}

function string(value: unknown, path: string): asserts value is string {
  if (typeof value !== 'string') throw new Error(`${path} must be a string`);
}

function optionalString(value: unknown, path: string): void {
  if (value !== undefined) nonEmptyString(value, path);
}

function boolean(value: unknown, path: string): asserts value is boolean {
  if (typeof value !== 'boolean') throw new Error(`${path} must be a boolean`);
}

function integer(value: unknown, path: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${path} must be a non-negative safe integer`);
  }
}

function enumValue<T extends string>(
  value: unknown,
  values: readonly T[],
  path: string
): asserts value is T {
  if (typeof value !== 'string' || !(values as readonly string[]).includes(value)) {
    throw new Error(`${path} must be one of ${values.join(', ')}`);
  }
}

function contentIdentity(value: unknown, path: string): void {
  const item = record(value, path);
  exactKeys(
    item,
    ['tree', 'sidecar', 'empty_directory_count', 'file_count', 'total_bytes'],
    path
  );
  nonEmptyString(item.tree, `${path}.tree`);
  nonEmptyString(item.sidecar, `${path}.sidecar`);
  if (item.empty_directory_count !== undefined) {
    integer(item.empty_directory_count, `${path}.empty_directory_count`);
  }
  if (item.file_count !== undefined) integer(item.file_count, `${path}.file_count`);
  if (item.total_bytes !== undefined) integer(item.total_bytes, `${path}.total_bytes`);
}

function divergenceOption(value: unknown, path: string): void {
  const item = record(value, path);
  exactKeys(item, ['expected_source', 'expected_destination'], path);
  contentIdentity(item.expected_source, `${path}.expected_source`);
  contentIdentity(item.expected_destination, `${path}.expected_destination`);
}

function divergenceResolution(value: unknown, path: string): void {
  const resolution = record(value, path);
  exactKeys(resolution, ['push', 'pull'], path);
  divergenceOption(resolution.push, `${path}.push`);
  divergenceOption(resolution.pull, `${path}.pull`);
}

function transferOutcome(value: unknown, path: string): void {
  const outcome = record(value, path);
  exactKeys(outcome, ['status', 'direction', 'proof', 'source', 'destination'], path);
  enumValue(outcome.status, ['succeeded', 'needs_resolution', 'failed'], `${path}.status`);
  if (outcome.direction !== undefined) {
    enumValue(outcome.direction, ['push', 'pull'], `${path}.direction`);
  }
  if (outcome.proof !== undefined) record(outcome.proof, `${path}.proof`);
  if (outcome.source !== undefined) contentIdentity(outcome.source, `${path}.source`);
  if (outcome.destination !== undefined) {
    contentIdentity(outcome.destination, `${path}.destination`);
  }
}

function operation(value: unknown, path: string): void {
  const item = record(value, path);
  exactKeys(
    item,
    [
      'id',
      'kind',
      'state',
      'accepted_revision',
      'operation_epoch',
      'reason',
      'message',
      'proof',
      'divergence_resolution',
    ],
    path
  );
  nonEmptyString(item.id, `${path}.id`);
  enumValue(item.kind, WORKSPACE_OPERATION_KINDS, `${path}.kind`);
  enumValue(item.state, WORKSPACE_OPERATION_STATES, `${path}.state`);
  integer(item.accepted_revision, `${path}.accepted_revision`);
  integer(item.operation_epoch, `${path}.operation_epoch`);
  optionalString(item.reason, `${path}.reason`);
  optionalString(item.message, `${path}.message`);
  if (item.proof !== undefined) record(item.proof, `${path}.proof`);
  if (item.divergence_resolution !== undefined) {
    divergenceResolution(item.divergence_resolution, `${path}.divergence_resolution`);
  }
}

function actions(value: unknown, path: string): void {
  const items = record(value, path);
  for (const name of WORKSPACE_ACTIONS) {
    if (!(name in items)) throw new Error(`${path}.${name} is required`);
    const availability = record(items[name], `${path}.${name}`);
    exactKeys(availability, ['available', 'refusal'], `${path}.${name}`);
    boolean(availability.available, `${path}.${name}.available`);
    if (availability.available) {
      if (availability.refusal !== undefined) {
        throw new Error(`${path}.${name}.refusal must be absent when the action is available`);
      }
      continue;
    }
    const refusal = record(availability.refusal, `${path}.${name}.refusal`);
    exactKeys(
      refusal,
      ['reason', 'message', 'operation_id', 'operation_kind'],
      `${path}.${name}.refusal`
    );
    enumValue(refusal.reason, WORKSPACE_REFUSAL_REASONS, `${path}.${name}.refusal.reason`);
    nonEmptyString(refusal.message, `${path}.${name}.refusal.message`);
    optionalString(refusal.operation_id, `${path}.${name}.refusal.operation_id`);
    if (refusal.operation_kind !== undefined) {
      enumValue(
        refusal.operation_kind,
        WORKSPACE_OPERATION_KINDS,
        `${path}.${name}.refusal.operation_kind`
      );
    }
  }
  for (const name of Object.keys(items)) enumValue(name, WORKSPACE_ACTIONS, `${path} key`);
}

export function parseWorkspaceProjection(
  value: unknown,
  path = 'workspace projection'
): WorkspaceProjection {
  const projection = record(value, path);
  exactKeys(
    projection,
    [
      'workspace_id',
      'name',
      'workspace_root',
      'revision',
      'operation_epoch',
      'lifecycle',
      'assignment',
      'filesystem',
      'workstation',
      'index',
      'capabilities',
      'actions',
      'operation',
      'binding',
      'created_at',
      'updated_at',
    ],
    path
  );
  nonEmptyString(projection.workspace_id, `${path}.workspace_id`);
  nonEmptyString(projection.name, `${path}.name`);
  // N-1 deployables omit the field (la ADR-5ced9)
  if (projection.workspace_root !== undefined) {
    string(projection.workspace_root, `${path}.workspace_root`);
  }
  integer(projection.revision, `${path}.revision`);
  integer(projection.operation_epoch, `${path}.operation_epoch`);
  enumValue(projection.lifecycle, WORKSPACE_LIFECYCLES, `${path}.lifecycle`);

  const assignment = record(projection.assignment, `${path}.assignment`);
  exactKeys(
    assignment,
    ['routing_class', 'filesystem_id', 'workstation_id', 'epoch'],
    `${path}.assignment`
  );
  enumValue(
    assignment.routing_class,
    WORKSPACE_ROUTING_CLASSES,
    `${path}.assignment.routing_class`
  );
  nonEmptyString(assignment.filesystem_id, `${path}.assignment.filesystem_id`);
  nonEmptyString(assignment.workstation_id, `${path}.assignment.workstation_id`);
  integer(assignment.epoch, `${path}.assignment.epoch`);

  const filesystem = record(projection.filesystem, `${path}.filesystem`);
  exactKeys(filesystem, ['id', 'kind', 'state', 'failure_reason'], `${path}.filesystem`);
  nonEmptyString(filesystem.id, `${path}.filesystem.id`);
  enumValue(filesystem.kind, WORKSPACE_FILESYSTEM_KINDS, `${path}.filesystem.kind`);
  enumValue(filesystem.state, WORKSPACE_FILESYSTEM_STATES, `${path}.filesystem.state`);
  optionalString(filesystem.failure_reason, `${path}.filesystem.failure_reason`);

  const workstation = record(projection.workstation, `${path}.workstation`);
  exactKeys(
    workstation,
    [
      'id',
      'kind',
      'status',
      'device_id',
      'wakeable',
      'environment_unsaved',
      'environment_snapshot_state',
      'failure_reason',
    ],
    `${path}.workstation`
  );
  nonEmptyString(workstation.id, `${path}.workstation.id`);
  enumValue(workstation.kind, WORKSPACE_WORKSTATION_KINDS, `${path}.workstation.kind`);
  enumValue(workstation.status, WORKSPACE_WORKSTATION_STATUSES, `${path}.workstation.status`);
  optionalString(workstation.device_id, `${path}.workstation.device_id`);
  boolean(workstation.wakeable, `${path}.workstation.wakeable`);
  boolean(workstation.environment_unsaved, `${path}.workstation.environment_unsaved`);
  if (workstation.environment_snapshot_state !== undefined) {
    enumValue(
      workstation.environment_snapshot_state,
      ['pending', 'ready', 'missing', 'unsupported', 'corrupt', 'failed'],
      `${path}.workstation.environment_snapshot_state`
    );
  }
  optionalString(workstation.failure_reason, `${path}.workstation.failure_reason`);

  const index = record(projection.index, `${path}.index`);
  exactKeys(
    index,
    ['progress', 'health', 'emitter_epoch', 'index_revision', 'queued', 'embedding', 'indexed'],
    `${path}.index`
  );
  enumValue(index.progress, WORKSPACE_INDEX_PROGRESS, `${path}.index.progress`);
  enumValue(index.health, WORKSPACE_INDEX_HEALTH, `${path}.index.health`);
  integer(index.emitter_epoch, `${path}.index.emitter_epoch`);
  integer(index.index_revision, `${path}.index.index_revision`);
  integer(index.queued, `${path}.index.queued`);
  integer(index.embedding, `${path}.index.embedding`);
  integer(index.indexed, `${path}.index.indexed`);

  const capabilities = record(projection.capabilities, `${path}.capabilities`);
  const capabilityKeys = [
    'content_readable',
    'content_writable',
    'execution',
    'search_current',
    'version_history',
  ] as const;
  exactKeys(capabilities, capabilityKeys, `${path}.capabilities`);
  for (const name of capabilityKeys) {
    boolean(capabilities[name], `${path}.capabilities.${name}`);
  }

  actions(projection.actions, `${path}.actions`);
  if (projection.operation !== null) operation(projection.operation, `${path}.operation`);
  if (projection.binding !== null) {
    const binding = record(projection.binding, `${path}.binding`);
    exactKeys(
      binding,
      [
        'device_id',
        'folder_path',
        'state',
        'connected',
        'last_outcome',
        'divergence_resolution',
      ],
      `${path}.binding`
    );
    nonEmptyString(binding.device_id, `${path}.binding.device_id`);
    nonEmptyString(binding.folder_path, `${path}.binding.folder_path`);
    enumValue(binding.state, WORKSPACE_BINDING_STATES, `${path}.binding.state`);
    boolean(binding.connected, `${path}.binding.connected`);
    if (binding.last_outcome !== undefined) {
      transferOutcome(binding.last_outcome, `${path}.binding.last_outcome`);
    }
    if (binding.divergence_resolution !== undefined) {
      divergenceResolution(
        binding.divergence_resolution,
        `${path}.binding.divergence_resolution`
      );
    }
  }
  nonEmptyString(projection.created_at, `${path}.created_at`);
  nonEmptyString(projection.updated_at, `${path}.updated_at`);
  return projection as unknown as WorkspaceProjection;
}

export function parseWorkspaceNameEntryList(value: unknown): WorkspaceNameEntry[] {
  if (!Array.isArray(value)) throw new Error('workspace name list must be an array');
  return value.map((item, index) => {
    const path = `workspace name list[${index}]`;
    const entry = record(item, path);
    exactKeys(entry, ['workspace_id', 'name', 'lifecycle'], path);
    nonEmptyString(entry.workspace_id, `${path}.workspace_id`);
    nonEmptyString(entry.name, `${path}.name`);
    enumValue(entry.lifecycle, WORKSPACE_LIFECYCLES, `${path}.lifecycle`);
    return {
      workspace_id: entry.workspace_id,
      name: entry.name,
      lifecycle: entry.lifecycle,
    };
  });
}

export function parseWorkspaceProjectionList(value: unknown): WorkspaceProjection[] {
  if (!Array.isArray(value)) throw new Error('workspace projection list must be an array');
  return value.map((item, index) =>
    parseWorkspaceProjection(item, `workspace projection list[${index}]`)
  );
}

export function workspaceProjectionFromBridgeFrame(frame: unknown): WorkspaceProjection | null {
  const candidate = record(frame, 'workspace bridge frame');
  if (candidate.method !== 'workspace.stateChanged') return null;
  if (candidate.jsonrpc !== '2.0') throw new Error('workspace bridge frame.jsonrpc must be 2.0');
  return parseWorkspaceProjection(candidate.params, 'workspace.stateChanged params');
}
